export type TimelineUser = {
	id: string;
	firstName?: string;
	lastName?: string;
	/** Auth roles (e.g. consultant, customer_user, organisation_admin). */
	roles?: string[];
};

export type TimelineFieldChange = {
	field: string;
	/** Dot paths on nested documents, e.g. `profile.pool`. */
	from?: unknown;
	to?: unknown;
	/**
	 * The field changed but its values are withheld — the row renders as
	 * "Personnummer uppdaterat" with no before/after. Set server-side for fields
	 * whose values must not reach the timeline at all.
	 */
	redacted?: boolean;
};

/**
 * One entry on an event timeline ("Händelser"), as returned by the history
 * endpoint of whichever service owns the entity.
 *
 * `type` is deliberately a plain string, not a union: the event catalogues are
 * curated per entity and change over time, and a dashboard running ahead of (or
 * behind) a backend deploy has to render what it is given. Unknown types fall
 * back to a generic label rather than breaking the timeline.
 */
export type TimelineEntry = {
	id: string;
	type: string;
	date: string;
	/** Which collection the entry came from, when a timeline merges more than one. */
	source?: string;
	/** Acting user, name expanded server-side. Absent for system events. */
	user?: TimelineUser;
	/**
	 * How the user made the change when not by hand in the dashboard, e.g.
	 * `ai-assistant` for a tool call the user approved in the assistant. Renders
	 * as "via AI-assistenten" after the actor name; unknown values render as-is.
	 */
	via?: string;
	/**
	 * Free-form event payload. Common shapes: `{ changes }` for updates,
	 * `{ key, name }` for tags and certifications, `{ branch, service, skill }`
	 * for competences, `{ from, to }` for promoted single-field changes.
	 */
	args?: {
		changes?: TimelineFieldChange[];
		[key: string]: unknown;
	};
};

/**
 * Event type for a notification (push/mail/SMS) handed off to a recipient.
 *
 * Merged into the consultant timeline from oo-notification-service's message
 * log, not written to `consultant_history` — hence the separate `source` on the
 * entry. Only present when the history endpoint is called with
 * `includeMessages=true`.
 */
export const NOTIFICATION_SENT = "NOTIFICATION_SENT";

/** The channels a notification can be handed off to. */
export type NotificationChannel = "push" | "mail" | "sms";

/**
 * `args` of a {@link NOTIFICATION_SENT} entry.
 *
 * The template is sent as a pair: `humanReadableName` is resolved against the
 * organisation's live notification templates server-side, `name` is the raw
 * template key it falls back to when the template has since been renamed away
 * or deleted — the same "live name, snapshot fallback" convention the rest of
 * the timeline uses for tag and certification keys.
 *
 * "Sent" means handed off to the push/mail/SMS provider. Delivery is not
 * observable, so nothing here ever claims it.
 */
export type NotificationSentArgs = {
	template: {
		name: string;
		humanReadableName?: string;
	};
	channels: NotificationChannel[];
	/** The rendered message text, stored in full and truncated for display. */
	body?: string;
	/** Mail subject or push title. */
	subject?: string;
};

/** One page of a timeline, as returned by the paged history endpoints. */
export type TimelinePage<T = TimelineEntry> = {
	entries: T[];
	hasMore: boolean;
	nextCursor?: string;
};
